/**
 * Automated Depreciation Service
 * Handles automated monthly depreciation posting with email notifications
 *
 * @module autoDepreciationService
 * @description This service manages the automated monthly depreciation process:
 * - Runs on the 1st of each month (or configured day)
 * - Posts depreciation for all active assets
 * - Sends email notifications to finance team
 * - Maintains audit log
 * - Handles errors gracefully
 */

import { supabase } from '@/lib/supabase'
import DepreciationService from './depreciationService'

// =====================================================================
// TYPES
// =====================================================================

export interface AutoDepreciationConfig {
  enabled: boolean
  day: number // 1-28
  notificationEmails: string[]
  lastRun?: string
  lastStatus?: 'success' | 'partial_success' | 'failed'
  lastResult?: AutoDepreciationResult
}

export interface AutoDepreciationResult {
  churchId: string
  churchName: string
  success: boolean
  assetsProcessed: number
  totalDepreciation: number
  journalEntryId?: string
  errors: Array<{
    assetId: string
    assetName: string
    error: string
  }>
  executionTimeMs: number
}

export interface DepreciationLogEntry {
  id: string
  churchId: string
  runDate: string
  periodDate: string
  status: 'success' | 'partial_success' | 'failed'
  assetsProcessed: number
  totalDepreciation: number
  journalEntryId?: string
  errors: any[]
  executionTimeMs: number
  notificationSent: boolean
  notificationSentAt?: string
  createdAt: string
}

export interface PendingDepreciationMonth {
  periodDate: string
  periodName: string
  assetsCount: number
  estimatedDepreciation: number
  isPosted: boolean
}

// =====================================================================
// SERVICE CLASS
// =====================================================================

export class AutoDepreciationService {
  /**
   * Run automated depreciation for all eligible churches
   * This is called by the cron job on the 1st of each month
   */
  static async runAutomatedDepreciation(
    periodDate: Date = new Date()
  ): Promise<AutoDepreciationResult[]> {
    const results: AutoDepreciationResult[] = []

    try {
      console.log('[AutoDepreciation] Starting automated depreciation run...', {
        periodDate: periodDate.toISOString()
      })

      // Get all churches with auto-depreciation enabled
      const { data: churches, error } = await supabase
        .from('churches')
        .select(`
          id,
          name,
          church_settings!inner(
            auto_depreciation_enabled,
            auto_depreciation_day,
            depreciation_notification_emails
          )
        `)
        .eq('church_settings.auto_depreciation_enabled', true)

      if (error) {
        console.error('[AutoDepreciation] Error fetching churches:', error)
        throw error
      }

      if (!churches || churches.length === 0) {
        console.log('[AutoDepreciation] No churches with auto-depreciation enabled')
        return results
      }

      console.log(`[AutoDepreciation] Found ${churches.length} churches with auto-depreciation enabled`)

      // Process each church
      for (const church of churches) {
        const startTime = Date.now()

        console.log(`[AutoDepreciation] Processing church: ${church.name} (${church.id})`)

        try {
          // Check if depreciation is needed for this period
          const isNeeded = await this.isDepreciationNeeded(church.id, periodDate)

          if (!isNeeded) {
            console.log(`[AutoDepreciation] No depreciation needed for church ${church.name}`)
            continue
          }

          // Process the church
          const result = await this.processChurchDepreciation(
            church.id,
            church.name,
            periodDate
          )

          const executionTime = Date.now() - startTime
          result.executionTimeMs = executionTime

          results.push(result)

          // Log to database
          await this.logDepreciationRun(church.id, periodDate, result)

          // Send notification email
          const notificationEmails = church.church_settings.depreciation_notification_emails || []
          if (result.success) {
            await this.sendSuccessNotification(church.name, notificationEmails, result)
          } else {
            await this.sendErrorNotification(church.name, notificationEmails, result)
          }

          // Update church settings
          await this.updateChurchSettings(church.id, result)

          console.log(`[AutoDepreciation] Completed church ${church.name}:`, {
            success: result.success,
            assetsProcessed: result.assetsProcessed,
            totalDepreciation: result.totalDepreciation
          })

        } catch (error) {
          console.error(`[AutoDepreciation] Error processing church ${church.id}:`, error)
          results.push({
            churchId: church.id,
            churchName: church.name,
            success: false,
            assetsProcessed: 0,
            totalDepreciation: 0,
            errors: [{
              assetId: '',
              assetName: '',
              error: error instanceof Error ? error.message : 'Unknown error'
            }],
            executionTimeMs: Date.now() - startTime
          })
        }
      }

      console.log('[AutoDepreciation] Automated depreciation run complete:', {
        total: results.length,
        successful: results.filter(r => r.success).length,
        failed: results.filter(r => !r.success).length
      })

      return results

    } catch (error) {
      console.error('[AutoDepreciation] Fatal error in runAutomatedDepreciation:', error)
      throw error
    }
  }

  /**
   * Process depreciation for a single church
   */
  private static async processChurchDepreciation(
    churchId: string,
    churchName: string,
    periodDate: Date
  ): Promise<AutoDepreciationResult> {
    // Use 'system' as the user ID for automated postings
    // You may want to create a system user in your database
    const systemUserId = 'system-auto-depreciation'

    try {
      console.log(`[AutoDepreciation] Posting depreciation for ${churchName}...`)

      // Use existing depreciation service
      const result = await DepreciationService.postDepreciationToLedger(
        churchId,
        periodDate,
        systemUserId
      )

      return {
        churchId,
        churchName,
        success: result.success,
        assetsProcessed: result.assets_processed,
        totalDepreciation: result.total_depreciation,
        journalEntryId: result.journal_entry_id,
        errors: result.errors,
        executionTimeMs: 0 // Will be set by caller
      }

    } catch (error) {
      console.error(`[AutoDepreciation] Error in processChurchDepreciation:`, error)
      return {
        churchId,
        churchName,
        success: false,
        assetsProcessed: 0,
        totalDepreciation: 0,
        errors: [{
          assetId: '',
          assetName: '',
          error: error instanceof Error ? error.message : 'Unknown error'
        }],
        executionTimeMs: 0
      }
    }
  }

  /**
   * Check if depreciation is needed for a period
   */
  private static async isDepreciationNeeded(
    churchId: string,
    periodDate: Date
  ): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .rpc('is_depreciation_needed', {
          p_church_id: churchId,
          p_period_date: periodDate.toISOString().split('T')[0]
        })

      if (error) throw error
      return data === true

    } catch (error) {
      console.error('[AutoDepreciation] Error checking if depreciation needed:', error)
      // Default to true to be safe
      return true
    }
  }

  /**
   * Log depreciation run to database
   */
  private static async logDepreciationRun(
    churchId: string,
    periodDate: Date,
    result: AutoDepreciationResult
  ): Promise<void> {
    try {
      const { error } = await supabase.from('auto_depreciation_log').insert({
        church_id: churchId,
        run_date: new Date().toISOString(),
        period_date: periodDate.toISOString().split('T')[0],
        status: result.success ? 'success' : (result.assetsProcessed > 0 ? 'partial_success' : 'failed'),
        assets_processed: result.assetsProcessed,
        total_depreciation: result.totalDepreciation,
        journal_entry_id: result.journalEntryId,
        errors: result.errors,
        execution_time_ms: result.executionTimeMs
      })

      if (error) {
        console.error('[AutoDepreciation] Error logging depreciation run:', error)
      }
    } catch (error) {
      console.error('[AutoDepreciation] Error in logDepreciationRun:', error)
    }
  }

  /**
   * Update church settings with last run info
   */
  private static async updateChurchSettings(
    churchId: string,
    result: AutoDepreciationResult
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('church_settings')
        .update({
          last_auto_depreciation_run: new Date().toISOString(),
          last_auto_depreciation_status: result.success ? 'success' : 'failed',
          last_auto_depreciation_result: result
        })
        .eq('church_id', churchId)

      if (error) {
        console.error('[AutoDepreciation] Error updating church settings:', error)
      }
    } catch (error) {
      console.error('[AutoDepreciation] Error in updateChurchSettings:', error)
    }
  }

  /**
   * Send success notification email
   */
  private static async sendSuccessNotification(
    churchName: string,
    emails: string[],
    result: AutoDepreciationResult
  ): Promise<void> {
    if (!emails || emails.length === 0) {
      console.log('[AutoDepreciation] No notification emails configured')
      return
    }

    const periodDate = new Date()
    const emailSubject = `✅ Depreciation Posted - ${periodDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`

    const emailBody = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #16a34a;">✅ Automated Depreciation Posted Successfully</h2>

        <div style="background-color: #f0fdf4; border-left: 4px solid #16a34a; padding: 16px; margin: 16px 0;">
          <p style="margin: 0;"><strong>Church:</strong> ${churchName}</p>
          <p style="margin: 8px 0 0 0;"><strong>Period:</strong> ${periodDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
        </div>

        <h3>Summary:</h3>
        <table style="width: 100%; border-collapse: collapse;">
          <tr style="background-color: #f9fafb;">
            <td style="padding: 12px; border: 1px solid #e5e7eb;">Assets Depreciated</td>
            <td style="padding: 12px; border: 1px solid #e5e7eb; text-align: right;"><strong>${result.assetsProcessed}</strong></td>
          </tr>
          <tr>
            <td style="padding: 12px; border: 1px solid #e5e7eb;">Total Depreciation</td>
            <td style="padding: 12px; border: 1px solid #e5e7eb; text-align: right;"><strong>${this.formatCurrency(result.totalDepreciation)}</strong></td>
          </tr>
          <tr style="background-color: #f9fafb;">
            <td style="padding: 12px; border: 1px solid #e5e7eb;">Journal Entry ID</td>
            <td style="padding: 12px; border: 1px solid #e5e7eb; text-align: right; font-family: monospace; font-size: 12px;">${result.journalEntryId || 'N/A'}</td>
          </tr>
          <tr>
            <td style="padding: 12px; border: 1px solid #e5e7eb;">Execution Time</td>
            <td style="padding: 12px; border: 1px solid #e5e7eb; text-align: right;">${result.executionTimeMs}ms</td>
          </tr>
        </table>

        <p style="margin-top: 24px;">View the journal entry in your FaithYoke accounting system.</p>

        <hr style="border: none; border-top: 1px solid #e5e7eb; margin: 24px 0;">
        <p style="color: #6b7280; font-size: 12px;">
          This is an automated message from FaithYoke Financial System.<br>
          Automated depreciation runs on the 1st of each month at 2am.
        </p>
      </div>
    `

    console.log('[AutoDepreciation] Success notification would be sent to:', emails)
    console.log('[AutoDepreciation] Email subject:', emailSubject)

    // TODO: Implement actual email sending
    // await sendEmail({
    //   to: emails,
    //   subject: emailSubject,
    //   html: emailBody
    // })
  }

  /**
   * Send error notification email
   */
  private static async sendErrorNotification(
    churchName: string,
    emails: string[],
    result: AutoDepreciationResult
  ): Promise<void> {
    if (!emails || emails.length === 0) {
      console.log('[AutoDepreciation] No notification emails configured')
      return
    }

    const periodDate = new Date()
    const emailSubject = `⚠️ Depreciation Failed - ${periodDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`

    const emailBody = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #dc2626;">⚠️ Automated Depreciation Failed</h2>

        <div style="background-color: #fef2f2; border-left: 4px solid #dc2626; padding: 16px; margin: 16px 0;">
          <p style="margin: 0;"><strong>Church:</strong> ${churchName}</p>
          <p style="margin: 8px 0 0 0;"><strong>Period:</strong> ${periodDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
        </div>

        <h3>Status:</h3>
        <p>Depreciation posting failed. Please review the errors below and post depreciation manually.</p>

        <h3>Errors:</h3>
        <ul style="background-color: #fef2f2; padding: 16px; border-radius: 4px;">
          ${result.errors.map(e => `
            <li style="margin: 8px 0;">
              <strong>${e.assetName || 'System Error'}:</strong> ${e.error}
            </li>
          `).join('')}
        </ul>

        ${result.assetsProcessed > 0 ? `
          <h3>Partial Success:</h3>
          <p><strong>${result.assetsProcessed}</strong> assets were successfully processed before the error occurred.</p>
        ` : ''}

        <div style="background-color: #fffbeb; border-left: 4px solid #f59e0b; padding: 16px; margin: 24px 0;">
          <p style="margin: 0;"><strong>Action Required:</strong> Please log in to your FaithYoke system and manually post depreciation for this period.</p>
        </div>

        <hr style="border: none; border-top: 1px solid #e5e7eb; margin: 24px 0;">
        <p style="color: #6b7280; font-size: 12px;">
          This is an automated message from FaithYoke Financial System.<br>
          If this error persists, please contact support.
        </p>
      </div>
    `

    console.log('[AutoDepreciation] Error notification would be sent to:', emails)
    console.log('[AutoDepreciation] Email subject:', emailSubject)

    // TODO: Implement actual email sending
    // await sendEmail({
    //   to: emails,
    //   subject: emailSubject,
    //   html: emailBody
    // })
  }

  /**
   * Format currency for display
   */
  private static formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)
  }

  // =====================================================================
  // PUBLIC API METHODS (for UI)
  // =====================================================================

  /**
   * Get auto-depreciation settings for a church
   */
  static async getSettings(churchId: string): Promise<AutoDepreciationConfig | null> {
    try {
      const { data, error } = await supabase
        .from('church_settings')
        .select(`
          auto_depreciation_enabled,
          auto_depreciation_day,
          depreciation_notification_emails,
          last_auto_depreciation_run,
          last_auto_depreciation_status,
          last_auto_depreciation_result
        `)
        .eq('church_id', churchId)
        .single()

      if (error) throw error
      if (!data) return null

      return {
        enabled: data.auto_depreciation_enabled || false,
        day: data.auto_depreciation_day || 1,
        notificationEmails: data.depreciation_notification_emails || [],
        lastRun: data.last_auto_depreciation_run,
        lastStatus: data.last_auto_depreciation_status,
        lastResult: data.last_auto_depreciation_result
      }
    } catch (error) {
      console.error('[AutoDepreciation] Error getting settings:', error)
      return null
    }
  }

  /**
   * Update auto-depreciation settings for a church
   */
  static async updateSettings(
    churchId: string,
    config: Partial<AutoDepreciationConfig>
  ): Promise<void> {
    const updateData: any = {}

    if (config.enabled !== undefined) {
      updateData.auto_depreciation_enabled = config.enabled
    }
    if (config.day !== undefined) {
      updateData.auto_depreciation_day = config.day
    }
    if (config.notificationEmails !== undefined) {
      updateData.depreciation_notification_emails = config.notificationEmails
    }

    const { error } = await supabase
      .from('church_settings')
      .upsert({
        church_id: churchId,
        ...updateData,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'church_id'
      })

    if (error) throw error

    console.log('[AutoDepreciation] Settings updated:', { churchId, config })
  }

  /**
   * Get depreciation log for a church
   */
  static async getLog(
    churchId: string,
    limit: number = 12
  ): Promise<DepreciationLogEntry[]> {
    const { data, error } = await supabase
      .from('auto_depreciation_log')
      .select('*')
      .eq('church_id', churchId)
      .order('run_date', { ascending: false })
      .limit(limit)

    if (error) throw error
    return (data || []) as DepreciationLogEntry[]
  }

  /**
   * Get pending depreciation months
   */
  static async getPendingMonths(
    churchId: string,
    startDate?: Date,
    endDate?: Date
  ): Promise<PendingDepreciationMonth[]> {
    try {
      const { data, error } = await supabase
        .rpc('get_pending_depreciation_months', {
          p_church_id: churchId,
          p_start_date: startDate?.toISOString().split('T')[0] || null,
          p_end_date: endDate?.toISOString().split('T')[0] || null
        })

      if (error) throw error
      return (data || []) as PendingDepreciationMonth[]
    } catch (error) {
      console.error('[AutoDepreciation] Error getting pending months:', error)
      return []
    }
  }

  /**
   * Get dashboard statistics
   */
  static async getDashboardStats(churchId: string): Promise<any> {
    try {
      const { data, error } = await supabase
        .from('v_auto_depreciation_status')
        .select('*')
        .eq('church_id', churchId)
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('[AutoDepreciation] Error getting dashboard stats:', error)
      return null
    }
  }
}

export default AutoDepreciationService
